import { map } from 'lodash-es';
import { types } from 'mobx-state-tree';
import type { Instance } from 'mobx-state-tree';

import { TypedStoreConfig } from '../factories/store';
import { createApiAction } from '@/api/api-action-factory';
import {
    COUNTRIES_ENDPOINT,
    REGION_PER_COUNTRY_ID_ENDPOINT,
    DETAILS_FOR_COUNTRY_ID_ENDPOINT,
} from '@/api/endpoints';

const CountryModel = types.model('CountryModel', {
    countryCode: '',
    countryName: '',
});

const RegionModel = types.model('RegionModel', {
    regionCode: '',
    regionName: '',
});

const PostalCodeValidationModel = types.model('PostalCodeValidation', {
    regionPostalCodeRegex: '',
    regionPostalCodeReadableRegex: '',
});

const DetailsModel = types.model('DetailsModel', {
    regions: types.array(RegionModel),
    postalCodeValidation: types.optional(PostalCodeValidationModel, {}),
});

const CountriesRegionsStore = TypedStoreConfig({
    modelName: 'CountriesRegionsStore',
    properties: {
        rawCountries: types.array(CountryModel),
        selectedCountryCode: types.optional(types.string, ''),
        rawRegionsPerCountry: types.map(types.array(RegionModel)),
        rawDetailsPerCountry: types.map(types.optional(DetailsModel, {})),
    },
})
    .actions((self) => ({
        getCountries: createApiAction(self, {
            endpoint: COUNTRIES_ENDPOINT,
            target: 'rawCountries',
        }),
        getRegionsForCountry: createApiAction(self, {
            endpoint: REGION_PER_COUNTRY_ID_ENDPOINT,
            target: 'rawRegionsPerCountry',
            targetMapId: 'countryCode',
        }),
        getDetailsForCountry: createApiAction(self, {
            endpoint: DETAILS_FOR_COUNTRY_ID_ENDPOINT,
            target: 'rawDetailsPerCountry',
            targetMapId: 'countryCode',
        }),
    }))
    .actions((self) => ({
        afterCreate() {
            self.getCountries();
        },
        retrieveDetails(countryCode: string) {
            const regions = self.rawDetailsPerCountry.get(countryCode);
            if (!regions) {
                self.getDetailsForCountry({ countryCode });
            }
        },
        retrieveRegions(countryCode: string) {
            const regions = self.rawRegionsPerCountry.get(countryCode);
            if (!regions) {
                self.getRegionsForCountry({ countryCode });
            }
        },
    }))
    .actions((self) => ({
        setStoreCountryCode(countryCode: string) {
            if (countryCode !== undefined) {
                self.selectedCountryCode = countryCode;

                if (!self.rawDetailsPerCountry.get(self.selectedCountryCode)) {
                    self.retrieveDetails(countryCode);
                }
                if (!self.rawRegionsPerCountry.get(self.selectedCountryCode)) {
                    self.retrieveRegions(countryCode);
                }
            }
        },
    }))
    .views((self) => ({
        countries() {
            return map(self.rawCountries, (value) => ({
                key: value.countryCode,
                value: value.countryName,
            }));
        },
        regions() {
            return map(
                self.rawDetailsPerCountry.get(self.selectedCountryCode)?.regions,
                (value) => ({
                    key: value.regionCode,
                    value: value.regionName,
                })
            );
        },
        zipMask() {
            return self.rawDetailsPerCountry.get(self.selectedCountryCode)?.postalCodeValidation;
        },
    }));

export type TypeCountriesRegionsStore = Instance<typeof CountriesRegionsStore>;

export default CountriesRegionsStore;
